<template>
	<div class="w-full">
		<div ref="emblaRef" class="overflow-hidden">
			<!-- safe center is a workaround for centering embla content without breaking sizing -->
			<div class="flex" style="justify-content: safe center;">
				<div v-for="post in posts" :key="post.permalink" class="h-96 basis-full xs:basis-72f shrink-0 grow-0 relative xs:mx-1 post">
					<img v-if="post.mediaType === 'IMAGE'" class="w-full h-full object-cover" :src="post.mediaUrl" :alt="cleanCaption(post.caption)" />
					<div v-else-if="post.mediaType === 'VIDEO'" class="w-full h-full">
						<video class="w-full h-full object-cover"
							:src="post.mediaUrl" :poster="post.thumbnailUrl"
							playsinline preload="metadata" loading="lazy"
							:aria-label="cleanCaption(post.caption)" :data-permalink="post.permalink"
							@play="onPlay(post)"
							@pause="onPause(post)"
							@ended="onEnded(post)"
							@timeupdate="onTimeUpdate($event, post)"
							@volumechange="onVolumeChange($event, post)"></video>
					</div>

					<div class="w-full absolute top-0 p-4 h-full transition-[opacity] opacity-0 hover:opacity-100 focus-within:opacity-100 text-font-body flex flex-col"
						style="background: #625952cc;">
						<a class="flex-1 flex flex-col min-h-[0]" :href="post.permalink" target="_blank" rel="noopener">
							<div class="flex-1 overflow-y-auto">
								<div>
									<p v-for="(line, i) in captionLines(post.caption)" :key="i">{{ line }}</p>
								</div>
							</div>
							<span>{{ post.date }}</span>
						</a>

						<!-- prevent clicks from focusing the shade, which would pin it open via focus-within -->
						<div v-if="post.mediaType === 'VIDEO'" class="flex h-10 shrink-0 items-center gap-2 -mx-4 -mb-4 px-2 text-white" @mousedown.prevent>
							<button type="button" class="w-6" @click="togglePlay(post)"
								:aria-label="playerState(post).playing ? 'Pause video' : 'Play video'">
								<!-- v-if breaks with fontawesome, it replaces the <i> with an <svg> in the DOM outside of vue -->
								<span v-show="playerState(post).playing"><i class="fas fa-pause"></i></span>
								<span v-show="!playerState(post).playing"><i class="fas fa-play"></i></span>
							</button>
							<progress class="video-seek flex-1" :value="playerState(post).progress" max="100" aria-label="Seek video"
								@click="seek($event, post)"></progress>
							<button type="button" class="w-6" @click="toggleMute(post)"
								:aria-label="playerState(post).muted ? 'Unmute video' : 'Mute video'">
								<span v-show="playerState(post).muted"><i class="fas fa-volume-mute"></i></span>
								<span v-show="!playerState(post).muted"><i class="fas fa-volume-up"></i></span>
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<button class="left relative disabled:opacity-50"
			v-show="!atStart || !atEnd" @click="scrollPrev"
			:disabled="atStart">
			<img src="/assets/svg/left-blue.svg" height="36" width="36" alt="Left"/>
		</button>
		<button class="right relative disabled:opacity-50"
			v-show="!atStart || !atEnd" @click="scrollNext"
			:disabled="atEnd">
			<img src="/assets/svg/right-blue.svg" height="36" width="36" alt="Right"/>
		</button>
	</div>
</template>

<script>
import EmblaCarousel from "embla-carousel";

export default {
	name: "SocialMediaCarousel",
	props: {
		posts: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			carousel: null,
			atStart: false,
			atEnd: false,
			players: {},
		}
	},
	created() {
		this.posts.forEach((post) => {
			if (post.mediaType === "VIDEO") {
				this.$set(this.players, post.permalink, {
					playing: false,
					muted: false,
					progress: 0,
				});
			}
		});
	},
	mounted() {
		this.carousel = EmblaCarousel(this.$refs.emblaRef, {
			skipSnaps: true,
		});

		this.updateScrollState();
		this.carousel.on("select", this.updateScrollState);
		this.carousel.on("resize", this.updateScrollState);
	},
	beforeDestroy() {
		this.carousel.destroy();
	},
	methods: {
		updateScrollState() {
			this.atStart = !this.carousel.canScrollPrev();
			this.atEnd = !this.carousel.canScrollNext();
		},
		scrollPrev() {
			this.carousel.scrollPrev();
		},
		scrollNext() {
			this.carousel.scrollNext();
		},
		cleanCaption(caption) {
			return (caption || "")
				.replace(/#\S+/g, "")
				.trim();
		},
		captionLines(caption) {
			return this.cleanCaption(caption).split("\n");
		},
		playerState(post) {
			return this.players[post.permalink];
		},
		videoEl(post) {
			return this.$el.querySelector(`video[data-permalink="${post.permalink}"]`);
		},
		togglePlay(post) {
			const video = this.videoEl(post);
			if (!video) return;
			if (video.paused) {
				// keep a single video playing at a time
				this.$el.querySelectorAll("video").forEach((other) => {
					if (other !== video) other.pause();
				});
				video.play().catch(() => {});
			} else {
				video.pause();
			}
		},
		toggleMute(post) {
			const video = this.videoEl(post);
			if (video) video.muted = !video.muted;
		},
		seek(event, post) {
			const video = this.videoEl(post);
			if (!video || !video.duration) return;
			const bar = event.currentTarget.getBoundingClientRect();
			video.currentTime = ((event.clientX - bar.left) / bar.width) * video.duration;
		},
		onPlay(post) {
			this.playerState(post).playing = true;
		},
		onPause(post) {
			this.playerState(post).playing = false;
		},
		onEnded(post) {
			const state = this.playerState(post);
			state.playing = false;
			state.progress = 0;
		},
		onTimeUpdate(event, post) {
			if (event.target.duration) {
				this.playerState(post).progress = (event.target.currentTime / event.target.duration) * 100;
			}
		},
		onVolumeChange(event, post) {
			this.playerState(post).muted = event.target.muted;
		},
	}
};
</script>

<style scoped>
.video-seek {
	-webkit-appearance: none;
	appearance: none;
	height: 0.25rem;
	border-radius: 9999px;
	background: rgba(255, 255, 255, 0.3);
	cursor: pointer;
}
.video-seek::-webkit-progress-bar {
	background: rgba(255, 255, 255, 0.3);
	border-radius: 9999px;
}
.video-seek::-webkit-progress-value {
	background: #ffffff;
	border-radius: 9999px;
}
.video-seek::-moz-progress-bar {
	background: #ffffff;
	border-radius: 9999px;
}
</style>
